import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    type: { type: String, default: "bar" },
    labels: Array,
    datasets: Array,
    options: { type: Object, default: {} }
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const ctx = this.element.getContext('2d')

    const defaultOptions = this.getDefaultOptions()
    const mergedOptions = this.deepMerge(defaultOptions, this.optionsValue)

    this.chart = new Chart(ctx, {
      type: this.typeValue,
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue.map((dataset, index) => ({
          ...this.getDefaultDatasetStyle(index),
          ...dataset
        }))
      },
      options: mergedOptions
    })
  }

  getDefaultOptions() {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.datasetsValue.length > 1,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              family: "'Pretendard', sans-serif",
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#111827',
          bodyColor: '#374151',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          titleFont: {
            family: "'Pretendard', sans-serif",
            size: 13,
            weight: '600'
          },
          bodyFont: {
            family: "'Pretendard', sans-serif",
            size: 12
          }
        }
      }
    }

    if (this.typeValue === 'bar' || this.typeValue === 'line') {
      baseOptions.scales = {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              family: "'Pretendard', sans-serif",
              size: 11
            }
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#F3F4F6'
          },
          ticks: {
            font: {
              family: "'Pretendard', sans-serif",
              size: 11
            }
          }
        }
      }
    }

    if (this.typeValue === 'doughnut' || this.typeValue === 'pie') {
      baseOptions.plugins.legend.display = true
      baseOptions.cutout = this.typeValue === 'doughnut' ? '60%' : 0
    }

    return baseOptions
  }

  getDefaultDatasetStyle(index) {
    const colors = [
      '#3182F6', '#00C9B5', '#FF9500', '#F04452', '#9B59B6',
      '#03C75A', '#5856D6', '#FF6B6B', '#4ECDC4', '#45B7D1'
    ]

    const color = colors[index % colors.length]

    if (this.typeValue === 'line') {
      return {
        borderColor: color,
        backgroundColor: `${color}20`,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    }

    if (this.typeValue === 'bar') {
      return {
        backgroundColor: `${color}CC`,
        hoverBackgroundColor: color,
        borderRadius: 6,
        borderSkipped: false
      }
    }

    if (this.typeValue === 'doughnut' || this.typeValue === 'pie') {
      return {
        backgroundColor: colors.slice(0, this.labelsValue.length),
        hoverOffset: 4,
        borderWidth: 0
      }
    }

    return {}
  }

  deepMerge(target, source) {
    const output = { ...target }
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] })
          } else {
            output[key] = this.deepMerge(target[key], source[key])
          }
        } else {
          Object.assign(output, { [key]: source[key] })
        }
      })
    }
    return output
  }

  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item)
  }
}
